import React, { Suspense, ComponentType } from 'react'
// import React, { lazy, Suspense } from 'react'
import { OmSpinner } from '@owma/kit'
import {
  Route,
  Navigate,
  Routes,
  useLocation,
  useParams,
  useNavigate,
  Location as RouterLocation,
  NavigateFunction,
} from 'react-router'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { connect } from 'react-redux'
import { history as globalHistory } from 'index'
import { getDefaultPage } from 'env'
import { parseSearchParam, processNParam } from 'utils'
import { isDevelop } from 'vars'

import Layout from 'layouts'

// Custom Location type from history.ts
type CustomLocation = Location

interface WithRouterOptions {
  navigate?: boolean
}

interface Match {
  params: Record<string, string | undefined>
  path: string
}

interface WithRouterProps {
  location: RouterLocation
  navigate: NavigateFunction
  history: typeof globalHistory
  match: Match
}

// Export RouteComponentProps as an alias to WithRouterProps for compatibility
export type RouteComponentProps<Params = Record<string, string | undefined>> = WithRouterProps & {
  match: Match & {
    params: Params
  }
}

export function withRouter<P extends object>(
  Component: ComponentType<P & Partial<WithRouterProps>>,
  options?: WithRouterOptions,
) {
  return (props: P) => {
    const newProps = {
      ...props,
    } as P & WithRouterProps

    newProps.location = useLocation()
    newProps.navigate = useNavigate()
    newProps.history = globalHistory
    newProps.match = {
      params: useParams(),
      path: newProps.location.pathname,
    }

    if (options != null && options.navigate === true) {
      newProps.navigate = useNavigate()
    }

    return <Component {...newProps} />
  }
}

interface RouteData {
  path: string
  Component: ComponentType<any>
  exact?: boolean
}

interface MenuState {
  initialized: boolean
  routerData: RouteData[]
}

interface UserState {
  serverType?: string
}

interface AppSettingsState {
  routerAnimation: string
}

interface MapStateToPropsReturn {
  initialized: boolean
  myRoutes: RouteData[]
  routerAnimation: string
  serverType?: string
}

const mapStateToProps = ({
  menu,
  user,
  appsettings,
}: {
  menu: MenuState
  user: UserState
  appsettings: AppSettingsState
}): MapStateToPropsReturn => ({
  initialized: menu.initialized,
  myRoutes: menu.routerData,
  routerAnimation: appsettings.routerAnimation,
  serverType: user.serverType,
})

interface RouterProps extends MapStateToPropsReturn {
  history: typeof globalHistory
  location: RouterLocation
}

interface UnlistenCallback {
  (): void
}

class RouterComponent extends React.Component<RouterProps> {
  unsubscribeFromHistory: UnlistenCallback | null = null

  // React 19는 findDOMNode를 제거하므로 CSSTransition에 nodeRef를 명시한다.
  // SwitchTransition 기본 out-in 모드라 한 번에 하나의 라우트 div만 마운트 → 단일 ref로 충분.
  routeNodeRef = React.createRef<HTMLDivElement>()

  constructor(props: RouterProps) {
    super(props)
    // await localforage.setItem('app.router.location', '');

    const { history } = props
    this.unsubscribeFromHistory = history.listen(this.handleLocationChange)
    this.handleLocationChange(history.location as any)
  }

  // componentDidMount() {
  // }

  componentWillUnmount() {
    if (this.unsubscribeFromHistory) this.unsubscribeFromHistory()
  }

  handleLocationChange = (e: CustomLocation) => {
    const { history } = this.props
    console.log('ROUTE CHANGED', e, history)

    parseSearchParam()
    processNParam()
  }

  // const Router = ({ history, routerAnimation, serverType }) => {
  render = () => {
    const { location, initialized, myRoutes, routerAnimation } = this.props

    const defaultPage = getDefaultPage()

    // dev 전용 테스트 페이지(/dev/*)는 메뉴 초기화/로그인 이전에도 열려야 하므로 init 가드에서 제외한다.
    // 라우트 정의는 services/router(getDefaultRoutes)에, 미인증 접근 허용은 Layout(needAuth) 에서 처리한다.
    const isDevRoute = isDevelop && location.pathname.indexOf('/dev/') === 0

    if (
      initialized === false &&
      location.pathname.indexOf('/auth/') !== 0 &&
      isDevRoute === false
    ) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <OmSpinner size="large" />
        </div>
      )
    }

    return (
      <Layout location={location}>
        <SwitchTransition>
          <CSSTransition
            key={location.pathname}
            nodeRef={this.routeNodeRef}
            appear
            classNames={routerAnimation}
            timeout={routerAnimation === 'none' ? 0 : 300}
          >
            <Routes location={location}>
              <Route path="/" element={<Navigate to={defaultPage} />} />
              {myRoutes.map(({ path, Component }) => (
                <Route
                  path={path}
                  key={path}
                  element={
                    <Suspense>
                      <div className={routerAnimation} ref={this.routeNodeRef}>
                        <Component />
                      </div>
                    </Suspense>
                  }
                />
              ))}
              <Route path="*" element={<Navigate to="/auth/404" />} />
            </Routes>
          </CSSTransition>
        </SwitchTransition>
      </Layout>
    )
  }
}

const ConnectedRouter = connect(mapStateToProps)(RouterComponent)
export default withRouter(ConnectedRouter as any)
